import type { Check } from "./constants";

function detectBrowser(): Check {
  const ua = window.navigator.userAgent;
  const browsers: [string, RegExp][] = [
    ["Edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ["Opera", /OPR\/([\d.]+)/],
    ["Firefox", /(?:Firefox|FxiOS)\/([\d.]+)/],
    ["Chrome", /(?:Chrome|CriOS)\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, pattern] of browsers) {
    const match = ua.match(pattern);
    if (match) return { status: "pass", detail: `${name} ${match[1]}` };
  }
  return {
    status: "fail",
    detail: "Unsupported browser. Use the latest Chrome, Edge, Firefox or Safari.",
  };
}

function detectOS(): Check {
  const ua = window.navigator.userAgent;
  const systems: [string, RegExp][] = [
    ["Android", /Android/],
    ["iOS", /iPhone|iPad|iPod/],
    ["Windows", /Win/],
    ["MacOS", /Mac/],
    ["Linux", /Linux/],
  ];
  const found = systems.find(([, pattern]) => pattern.test(ua));
  return found
    ? { status: "pass", detail: found[0] }
    : { status: "fail", detail: "Unknown operating system" };
}

function detectScreen(): Check {
  const { width, height } = window.screen;
  return width > 350
    ? { status: "pass", detail: `${width}x${height}` }
    : { status: "fail", detail: `${width}x${height} is too small to take an exam` };
}

function detectSupport(): Check {
  const missing = [
    !window.isSecureContext && "a secure (https) connection",
    !navigator.mediaDevices?.getUserMedia && "camera and microphone access",
    !(window as any).MediaRecorder && "recording",
    typeof WebAssembly !== "object" && "WebAssembly",
  ].filter(Boolean);
  return missing.length
    ? { status: "fail", detail: `Your browser is missing: ${missing.join(", ")}` }
    : { status: "pass", detail: "Camera, microphone and recording are supported" };
}

async function detectInternet(): Promise<Check> {
  if (!navigator.onLine) return { status: "fail", detail: "You are offline" };
  try {
    const startedAt = performance.now();
    await fetch(`${window.location.origin}/?_=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
    });
    return {
      status: "pass",
      detail: `Connected (${Math.round(performance.now() - startedAt)} ms)`,
    };
  } catch {
    return { status: "fail", detail: "Could not reach the exam server" };
  }
}

export function mediaErrorMessage(err: any, device: string): string {
  switch (err?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return `${device} access denied. Allow ${device.toLowerCase()} permission in your browser and run the test again.`;
    case "NotFoundError":
    case "OverconstrainedError":
      return `No ${device.toLowerCase()} found on this device.`;
    case "NotReadableError":
      return `${device} is in use by another application.`;
    default:
      return `Could not access your ${device.toLowerCase()}.`;
  }
}

export const SYSTEM_DETECTORS: Record<string, () => Check | Promise<Check>> = {
  browser: detectBrowser,
  os: detectOS,
  screen: detectScreen,
  internet: detectInternet,
  support: detectSupport,
};
